import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { tasksApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import ForceSyncButton from '../components/ForceSyncButton';
import { 
  CalendarDays, 
  Users, 
  CheckCircle2, 
  Clock, 
  AlertTriangle,
  LogOut,
  Home,
  Calendar,
  ChevronRight,
  Map,
  ClipboardCheck,
  Shuffle,
  ThumbsUp,
  ThumbsDown,
  Image,
  History,
  X,
  Camera,
  Upload,
  Trash2,
  Mail,
  ClipboardList
} from 'lucide-react';
import { format, startOfWeek, endOfWeek } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get proper photo URL with auth token
const getPhotoUrlWithAuth = (photo, authToken) => {
  if (!photo) return '';
  // If already a full URL (Backblaze, Cloudinary, etc.), return as-is
  if (photo.startsWith('http://') || photo.startsWith('https://')) {
    return photo;
  }
  // For B2 photos or any other path, use the photos endpoint with auth token
  return `${API_URL}/api/photo?path=${encodeURIComponent(photo)}&auth=${authToken}`;
};

const GouvernantDashboard = () => {
  const { user, logout, token } = useAuth();
  
  // Create a getPhotoUrl function that includes the auth token
  // Fallback to localStorage token if context token is not available
  const getPhotoUrl = (photo) => {
    const authToken = token || localStorage.getItem('token');
    return getPhotoUrlWithAuth(photo, authToken);
  };
  
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    today: 0,
    week: 0,
    pending: 0,
    completed: 0,
    inProgress: 0,
    problems: 0
  });
  const [todayTasks, setTodayTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Quality control states
  const [controlStats, setControlStats] = useState(null);
  const [randomTask, setRandomTask] = useState(null);
  const [loadingRandom, setLoadingRandom] = useState(false);
  const [showControlModal, setShowControlModal] = useState(false);
  const [controlResult, setControlResult] = useState('');
  const [controlRating, setControlRating] = useState(null);
  const [controlComment, setControlComment] = useState('');
  const [controlPhotos, setControlPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [submittingControl, setSubmittingControl] = useState(false);
  const [recentControls, setRecentControls] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showTaskSelector, setShowTaskSelector] = useState(false);
  const fileInputRef = useRef(null);
  
  // Alertes retard FDM
  const [lateAlerts, setLateAlerts] = useState([]);
  const [showLateAlerts, setShowLateAlerts] = useState(true);
  
  // Templates de commentaires rapides pour le contrôle qualité
  const quickCommentTemplates = [
    { label: "Excellent travail", text: "Excellent travail, ménage impeccable. Rien à signaler." },
    { label: "Bien mais...", text: "Bon travail dans l'ensemble. Quelques points à améliorer : " },
    { label: "Poussière", text: "Présence de poussière sur certaines surfaces (meubles, étagères)." },
    { label: "Salle de bain", text: "Salle de bain à revoir : traces de calcaire, miroir non nettoyé." },
    { label: "Cuisine", text: "Cuisine : plan de travail et électroménagers à nettoyer plus en profondeur." },
    { label: "Sols", text: "Sols : traces visibles, nécessite un passage supplémentaire." },
    { label: "Literie", text: "Literie : draps mal tendus / taies d'oreiller à changer." },
    { label: "Temps insuffisant", text: "Temps passé insuffisant par rapport à la taille du logement." },
    { label: "Photos manquantes", text: "Photos de contrôle insuffisantes ou floues." },
    { label: "Félicitations", text: "Félicitations pour ce travail soigné et rapide !" }
  ];

  const fetchControlStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/quality-controls/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setControlStats(data);
      }
    } catch (error) {
      console.error('Error fetching control stats:', error);
    }
  }, [token]);

  const fetchRecentControls = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/quality-controls?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRecentControls(data);
      }
    } catch (error) {
      console.error('Error fetching controls:', error);
    }
  }, [token]);

  const fetchData = useCallback(async () => {
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      const weekStart = format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd');
      const weekEnd = format(endOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd');

      // Get today's tasks
      const todayRes = await tasksApi.list({ date: today });
      setTodayTasks(todayRes.data);

      // Get week's tasks for stats
      const weekRes = await tasksApi.list({ date_from: weekStart, date_to: weekEnd });
      
      const allTasks = weekRes.data;
      setStats({
        today: todayRes.data.length,
        week: allTasks.length,
        pending: allTasks.filter(t => t.status === 'affectee').length,
        completed: allTasks.filter(t => t.status === 'termine').length,
        inProgress: allTasks.filter(t => t.status === 'en_cours').length,
        problems: allTasks.filter(t => t.status === 'probleme').length
      });
      
      // Vérifier les alertes de retard (après 13h)
      checkLateAlerts(todayRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Fonction pour vérifier les FDM en retard (après 13h, prestation non commencée)
  const checkLateAlerts = useCallback((tasks) => {
    const now = new Date();
    const currentHour = now.getHours();
    
    // Seulement après 13h
    if (currentHour < 13) {
      setLateAlerts([]);
      return;
    }
    
    // Filtrer les tâches assignées/acceptées mais pas encore commencées
    const lateTasks = tasks.filter(task => {
      const isNotStarted = ['affectee', 'acceptee', 'planifie'].includes(task.status);
      const hasHousekeeper = task.housekeeper_ids && task.housekeeper_ids.length > 0;
      return isNotStarted && hasHousekeeper;
    });
    
    // Grouper par FDM
    const alertsByFdm = {};
    lateTasks.forEach(task => {
      const fdmNames = task.housekeeper_names || ['FDM inconnue'];
      fdmNames.forEach((name, idx) => {
        const fdmId = task.housekeeper_ids?.[idx] || 'unknown';
        if (!alertsByFdm[fdmId]) {
          alertsByFdm[fdmId] = {
            fdmName: name,
            fdmId: fdmId,
            tasks: []
          };
        }
        alertsByFdm[fdmId].tasks.push({
          id: task.id,
          propertyName: task.property_name,
          status: task.status
        });
      });
    });
    
    setLateAlerts(Object.values(alertsByFdm));
  }, []);

  useEffect(() => {
    fetchData();
    fetchControlStats();
    fetchRecentControls();
  }, [fetchData, fetchControlStats, fetchRecentControls]);

  const generateRandomControl = async () => {
    setLoadingRandom(true);
    try {
      const response = await fetch(`${API_URL}/api/quality-controls/random`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRandomTask(data);
        setShowControlModal(true);
        setControlResult('');
        setControlComment('');
        setControlPhotos([]);
      } else {
        const error = await response.json();
        alert(error.detail || 'Aucune tâche disponible pour le contrôle');
      }
    } catch (error) {
      console.error('Error generating random control:', error);
      alert('Erreur lors de la génération du contrôle');
    } finally {
      setLoadingRandom(false);
    }
  };

  // Sélectionner manuellement une tâche pour le contrôle
  const selectTaskForControl = (task) => {
    setRandomTask(task);
    setShowTaskSelector(false);
    setShowControlModal(true);
    setControlResult('');
    setControlRating(null);
    setControlComment('');
    setControlPhotos([]);
  };

  // Filtrer les tâches terminées pour le sélecteur
  const completedTodayTasks = todayTasks.filter(t => t.status === 'termine');

  const handlePhotoUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    if (controlPhotos.length + files.length > 10) {
      alert('Maximum 10 photos autorisées');
      return;
    }
    
    setUploadingPhoto(true);
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await fetch(`${API_URL}/api/quality-controls/upload-photo`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        
        if (response.ok) {
          const data = await response.json();
          setControlPhotos(prev => [...prev, data.path]);
        } else {
          console.error('Failed to upload photo');
        }
      } catch (error) {
        console.error('Error uploading photo:', error);
      }
    }
    
    setUploadingPhoto(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeControlPhoto = (index) => {
    setControlPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const submitControl = async () => {
    if (!controlResult) {
      alert('Veuillez sélectionner un résultat');
      return;
    }
    if (controlRating === null) {
      alert('Veuillez attribuer une note /10');
      return;
    }
    
    setSubmittingControl(true);
    try {
      const response = await fetch(`${API_URL}/api/quality-controls`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          task_id: randomTask.id,
          housekeeper_id: randomTask.housekeeper_ids?.[0] || '',
          result: controlResult,
          rating: controlRating,
          comment: controlComment || null,
          control_photos: controlPhotos
        })
      });
      
      if (response.ok) {
        setShowControlModal(false);
        setRandomTask(null);
        setControlResult('');
        setControlRating(null);
        setControlComment('');
        setControlPhotos([]);
        fetchControlStats();
        fetchRecentControls();
        alert('Contrôle enregistré avec succès !');
      } else {
        const error = await response.json();
        alert(error.detail || 'Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      console.error('Error submitting control:', error);
      alert('Erreur lors de l\'enregistrement');
    } finally {
      setSubmittingControl(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      affectee: 'bg-blue-100 text-blue-700',
      en_cours: 'bg-amber-100 text-amber-700',
      termine: 'bg-emerald-100 text-emerald-700',
      probleme: 'bg-red-100 text-red-700'
    };
    const labels = {
      affectee: 'Affectée',
      en_cours: 'En cours',
      termine: 'Terminée',
      probleme: 'Problème'
    };
    return <Badge className={`${styles[status] || 'bg-gray-100'} border-0`}>{labels[status] || status}</Badge>;
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-[#003366] text-white px-4 py-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-mcb-full.png" alt="Ma Conciergerie Bretonne" className="h-12" />
            <div>
              <h1 className="font-bold text-lg">Espace Gouvernant</h1>
              <p className="text-blue-200 text-sm">{user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ForceSyncButton variant="compact" />
            <Button 
              variant="ghost" 
              className="text-white hover:bg-white/20"
              onClick={handleLogout}
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 md:p-8">
        {/* Welcome */}
        <div className="mb-8">
          <p className="text-slate-500 text-sm">
            {format(new Date(), "EEEE d MMMM yyyy", { locale: fr })}
          </p>
          <h2 className="text-2xl font-bold text-slate-800 mt-1">
            Bonjour, {user?.name?.split(' ')[0]} 👋
          </h2>
        </div>

        {/* Alertes Retard FDM (après 13h) */}
        {lateAlerts.length > 0 && showLateAlerts && (
          <Card className="border-0 shadow-lg bg-gradient-to-r from-red-500 to-orange-500 text-white mb-8" data-testid="late-alerts-card">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <AlertTriangle className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">⚠️ Alertes Retard - {lateAlerts.length} FDM</h3>
                    <p className="text-white/80 text-sm">Prestations du jour non commencées après 13h</p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="text-white hover:bg-white/20"
                  onClick={() => setShowLateAlerts(false)}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
              
              <div className="mt-4 space-y-3">
                {lateAlerts.map((alert) => (
                  <div key={alert.fdmId} className="bg-white/10 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="h-4 w-4" />
                      <span className="font-semibold">{alert.fdmName}</span>
                      <Badge className="bg-white/20 text-white border-0 text-xs">
                        {alert.tasks.length} prestation{alert.tasks.length > 1 ? 's' : ''}
                      </Badge>
                    </div>
                    <div className="space-y-1 ml-6">
                      {alert.tasks.map((task) => (
                        <div key={task.id} className="flex items-center gap-2 text-sm text-white/90">
                          <Home className="h-3 w-3" />
                          <span>{task.propertyName}</span>
                          <Badge className="bg-amber-400/30 text-white border-0 text-xs">
                            {task.status === 'affectee' ? 'Affectée' : task.status === 'acceptee' ? 'Acceptée' : 'Planifiée'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              
              <p className="mt-4 text-xs text-white/60 text-center">
                💡 Conseil : Contactez les FDM concernées pour vérifier leur situation
              </p>
            </CardContent>
          </Card>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="p-4 text-center">
              <CalendarDays className="h-8 w-8 mx-auto text-blue-500 mb-2" />
              <p className="text-2xl font-bold text-slate-800">{stats.today}</p>
              <p className="text-sm text-slate-500">Aujourd'hui</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="p-4 text-center">
              <Calendar className="h-8 w-8 mx-auto text-purple-500 mb-2" />
              <p className="text-2xl font-bold text-slate-800">{stats.week}</p>
              <p className="text-sm text-slate-500">Cette semaine</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="p-4 text-center">
              <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-500 mb-2" />
              <p className="text-2xl font-bold text-slate-800">{stats.completed}</p>
              <p className="text-sm text-slate-500">Terminées</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="p-4 text-center">
              <AlertTriangle className="h-8 w-8 mx-auto text-red-500 mb-2" />
              <p className="text-2xl font-bold text-slate-800">{stats.problems}</p>
              <p className="text-sm text-slate-500">Problèmes</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Actions Grid */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          {/* Planning Card */}
          <Card 
            className="border-0 shadow-lg bg-gradient-to-r from-[#003366] to-[#004488] text-white cursor-pointer hover:shadow-xl transition-shadow"
            onClick={() => navigate('/gouvernant/planning')}
            data-testid="planning-card"
          >
            <CardContent className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-xl">
                  <CalendarDays className="h-8 w-8" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Planning</h3>
                  <p className="text-blue-200">Jour, semaine, mois, année</p>
                </div>
              </div>
              <ChevronRight className="h-8 w-8" />
            </CardContent>
          </Card>

          {/* Map Card */}
          <Card 
            className="border-0 shadow-lg bg-gradient-to-r from-emerald-500 to-emerald-600 text-white cursor-pointer hover:shadow-xl transition-shadow"
            onClick={() => navigate('/gouvernant/carte')}
            data-testid="map-card"
          >
            <CardContent className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-xl">
                  <Map className="h-8 w-8" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Carte du jour</h3>
                  <p className="text-emerald-100">Voir tous les ménages sur la carte</p>
                </div>
              </div>
              <ChevronRight className="h-8 w-8" />
            </CardContent>
          </Card>

          {/* Property Tracking Card */}
          <Card 
            className="border-0 shadow-lg bg-gradient-to-r from-purple-500 to-purple-600 text-white cursor-pointer hover:shadow-xl transition-shadow"
            onClick={() => navigate('/gouvernant/suivi-logements')}
            data-testid="tracking-card"
          >
            <CardContent className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-xl">
                  <ClipboardList className="h-8 w-8" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Suivi logements</h3>
                  <p className="text-purple-100">Codes, clés, statuts</p>
                </div>
              </div>
              <ChevronRight className="h-8 w-8" />
            </CardContent>
          </Card>

          {/* Photo Gallery Card */}
          <Card 
            className="border-0 shadow-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white cursor-pointer hover:shadow-xl transition-shadow"
            onClick={() => navigate('/gouvernant/galerie')}
            data-testid="gallery-card"
          >
            <CardContent className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-xl">
                  <Camera className="h-8 w-8" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Galerie photos</h3>
                  <p className="text-amber-100">Toutes les photos de prestations</p>
                </div>
              </div>
              <ChevronRight className="h-8 w-8" />
            </CardContent>
          </Card>
        </div>

        {/* Quality Control Section */}
        <Card className="border-0 shadow-lg mb-8">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-[#003366]" />
              Contrôles Qualité
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Control Stats */}
            {controlStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-slate-800">{controlStats.total}</p>
                  <p className="text-xs text-slate-500">Total contrôles</p>
                </div>
                <div className="bg-emerald-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-emerald-600">{controlStats.validated}</p>
                  <p className="text-xs text-emerald-600">Validés</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-red-600">{controlStats.non_compliant}</p>
                  <p className="text-xs text-red-600">Non-conformes</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-blue-600">{controlStats.compliance_rate}%</p>
                  <p className="text-xs text-blue-600">Taux conformité</p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              <Button 
                onClick={generateRandomControl}
                disabled={loadingRandom}
                className="bg-[#003366] hover:bg-[#002244] text-white"
                data-testid="random-control-btn"
              >
                {loadingRandom ? (
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                ) : (
                  <Shuffle className="h-4 w-4 mr-2" />
                )}
                Contrôle aléatoire
              </Button>
              
              <Button 
                onClick={() => setShowTaskSelector(true)}
                disabled={completedTodayTasks.length === 0}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="select-control-btn"
              >
                <ClipboardList className="h-4 w-4 mr-2" />
                Choisir un logement ({completedTodayTasks.length})
              </Button>
              
              <Button 
                variant="outline"
                onClick={() => setShowHistory(true)}
                data-testid="history-btn"
              >
                <History className="h-4 w-4 mr-2" />
                Historique
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Today's Tasks */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Home className="h-5 w-5 text-[#003366]" />
              Prestations du jour
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center py-8 text-slate-500">Chargement...</p>
            ) : todayTasks.length === 0 ? (
              <p className="text-center py-8 text-slate-500">Aucune prestation aujourd'hui</p>
            ) : (
              <div className="space-y-3">
                {todayTasks.map(task => (
                  <div 
                    key={task.id} 
                    className="p-4 bg-slate-50 rounded-xl border border-slate-100"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: task.property_color || '#003366' }}
                          />
                          <span className="font-semibold text-slate-800">
                            {task.property_name}
                          </span>
                        </div>
                        <p className="text-sm text-slate-500">{task.property_city}</p>
                        {task.housekeeper_names?.length > 0 && (
                          <div className="flex items-center gap-1 mt-2 text-sm text-slate-600">
                            <Users className="h-4 w-4" />
                            {task.housekeeper_names.join(' & ')}
                          </div>
                        )}
                        {task.time_spent_minutes > 0 && (
                          <div className="flex items-center gap-1 mt-1 text-sm text-slate-500">
                            <Clock className="h-4 w-4" />
                            {task.time_spent_minutes} min
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {getStatusBadge(task.status)}
                        {task.status === 'termine' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => selectTaskForControl(task)}
                            className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 text-xs"
                            data-testid={`control-task-${task.id}`}
                          >
                            <ClipboardCheck className="h-3 w-3 mr-1" />
                            Contrôler
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Quality Control Modal */}
      {showControlModal && randomTask && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-slate-800">Contrôle Qualité</h2>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => setShowControlModal(false)}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Task Info */}
              <div className="bg-slate-50 rounded-xl p-4 mb-6">
                <h3 className="font-semibold text-slate-800 mb-2">{randomTask.property_name}</h3>
                <p className="text-sm text-slate-500 mb-1">{randomTask.property_address}</p>
                <p className="text-sm text-slate-500 mb-3">{randomTask.property_city}</p>
                
                <div className="flex flex-wrap gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Date:</span>
                    <span className="ml-1 font-medium">{format(new Date(randomTask.date), 'd MMM yyyy', { locale: fr })}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">FDM:</span>
                    <span className="ml-1 font-medium">{randomTask.housekeeper_names?.join(', ') || 'N/A'}</span>
                  </div>
                  {randomTask.time_spent_minutes && (
                    <div>
                      <span className="text-slate-500">Temps:</span>
                      <span className="ml-1 font-medium">{randomTask.time_spent_minutes} min</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Photos */}
              {randomTask.photos && randomTask.photos.length > 0 && (
                <div className="mb-6">
                  <p className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                    <Image className="h-4 w-4" />
                    Photos du ménage ({randomTask.photos.length})
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {randomTask.photos.map((photo, index) => (
                      <div 
                        key={`photo-${index}`}
                        className="aspect-square rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => setSelectedPhoto(getPhotoUrl(photo))}
                      >
                        <img 
                          src={getPhotoUrl(photo)}
                          alt={`Photo ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Control Result */}
              <div className="mb-6">
                <p className="text-sm font-medium text-slate-700 mb-3">Résultat du contrôle</p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                      controlResult === 'validated' 
                        ? 'border-emerald-500 bg-emerald-50' 
                        : 'border-slate-200 hover:border-emerald-300'
                    }`}
                    onClick={() => setControlResult('validated')}
                  >
                    <ThumbsUp className={`h-8 w-8 ${controlResult === 'validated' ? 'text-emerald-500' : 'text-slate-400'}`} />
                    <span className={`font-medium ${controlResult === 'validated' ? 'text-emerald-700' : 'text-slate-600'}`}>
                      Validé
                    </span>
                  </button>
                  <button
                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                      controlResult === 'non_compliant' 
                        ? 'border-red-500 bg-red-50' 
                        : 'border-slate-200 hover:border-red-300'
                    }`}
                    onClick={() => setControlResult('non_compliant')}
                  >
                    <ThumbsDown className={`h-8 w-8 ${controlResult === 'non_compliant' ? 'text-red-500' : 'text-slate-400'}`} />
                    <span className={`font-medium ${controlResult === 'non_compliant' ? 'text-red-700' : 'text-slate-600'}`}>
                      Non-conforme
                    </span>
                  </button>
                </div>
              </div>

              {/* Rating /10 */}
              <div className="mb-6">
                <p className="text-sm font-medium text-slate-700 mb-3">Note du ménage <span className="text-red-500">*</span></p>
                <div className="flex flex-wrap gap-2">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => setControlRating(num)}
                      className={`w-10 h-10 rounded-full font-bold text-sm transition-all ${
                        controlRating === num
                          ? num >= 8 
                            ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30' 
                            : num >= 5
                              ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/30'
                              : 'bg-red-500 text-white shadow-lg shadow-red-500/30'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                      data-testid={`rating-btn-${num}`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
                {controlRating !== null && (
                  <p className={`mt-2 text-sm font-medium ${
                    controlRating >= 8 ? 'text-emerald-600' : controlRating >= 5 ? 'text-amber-600' : 'text-red-600'
                  }`}>
                    Note sélectionnée : {controlRating}/10
                    {controlRating >= 8 && ' - Excellent !'}
                    {controlRating >= 5 && controlRating < 8 && ' - Correct'}
                    {controlRating < 5 && ' - À améliorer'}
                  </p>
                )}
              </div>

              {/* Comment */}
              <div className="mb-6">
                <label className="text-sm font-medium text-slate-700 mb-2 block flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Commentaire de contrôle
                </label>
                <p className="text-xs text-slate-500 mb-2">
                  Ce commentaire sera envoyé par email à la femme de ménage pour lui indiquer les points à améliorer.
                </p>
                
                {/* Templates de commentaires rapides */}
                <div className="mb-3">
                  <p className="text-xs font-medium text-slate-500 mb-2">💡 Commentaires rapides :</p>
                  <div className="flex flex-wrap gap-1.5">
                    {quickCommentTemplates.map((template, idx) => (
                      <button
                        key={template.text || `tpl-${idx}`}
                        type="button"
                        onClick={() => {
                          // Si le commentaire est vide, remplacer, sinon ajouter
                          if (!controlComment.trim()) {
                            setControlComment(template.text);
                          } else {
                            setControlComment(prev => prev + (prev.endsWith(' ') ? '' : ' ') + template.text);
                          }
                        }}
                        className="px-2 py-1 text-xs bg-slate-100 hover:bg-blue-100 text-slate-600 hover:text-blue-700 rounded-full border border-slate-200 hover:border-blue-300 transition-colors"
                        data-testid={`quick-comment-${idx}`}
                      >
                        {template.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                <textarea
                  value={controlComment}
                  onChange={(e) => setControlComment(e.target.value)}
                  className="w-full p-3 border border-slate-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#003366]"
                  rows={4}
                  placeholder="Ex: Merci pour votre travail. Points à améliorer : les draps étaient mal tirés, penser à bien nettoyer sous les meubles..."
                />
              </div>

              {/* Control Photos Upload */}
              <div className="mb-6">
                <label className="text-sm font-medium text-slate-700 mb-2 block flex items-center gap-2">
                  <Camera className="h-4 w-4" />
                  Photos (optionnel)
                </label>
                <p className="text-xs text-slate-500 mb-3">
                  Ajoutez des photos pour illustrer vos remarques
                </p>
                
                {/* Photo grid */}
                {controlPhotos.length > 0 && (
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    {controlPhotos.map((photo, index) => (
                      <div 
                        key={`control-photo-${index}`}
                        className="relative aspect-square rounded-lg overflow-hidden group"
                      >
                        <img 
                          src={getPhotoUrl(photo)}
                          alt={`Photo ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <button
                          onClick={() => removeControlPhoto(index)}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Upload button */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePhotoUpload}
                  accept="image/*"
                  multiple
                  className="hidden"
                />
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto || controlPhotos.length >= 10}
                  className="w-full"
                >
                  {uploadingPhoto ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-slate-400 border-t-transparent rounded-full mr-2" />
                      Upload en cours...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Ajouter des photos ({controlPhotos.length}/10)
                    </>
                  )}
                </Button>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowControlModal(false)}
                >
                  Annuler
                </Button>
                <Button
                  className="flex-1 bg-[#003366] hover:bg-[#002244]"
                  onClick={submitControl}
                  disabled={!controlResult || controlRating === null || submittingControl}
                >
                  {submittingControl ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistory && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-slate-800">Historique des contrôles</h2>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => setShowHistory(false)}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {recentControls.length === 0 ? (
                <p className="text-center py-8 text-slate-500">Aucun contrôle effectué</p>
              ) : (
                <div className="space-y-3">
                  {recentControls.map(control => (
                    <div 
                      key={control.id}
                      className={`p-4 rounded-xl border ${
                        control.result === 'validated' 
                          ? 'bg-emerald-50 border-emerald-200' 
                          : 'bg-red-50 border-red-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-semibold text-slate-800">{control.property_name}</p>
                          <p className="text-sm text-slate-500">{control.housekeeper_name}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            {format(new Date(control.controlled_at), "d MMM yyyy 'à' HH:mm", { locale: fr })}
                          </p>
                          {control.comment && (
                            <p className="text-sm text-slate-600 mt-2 italic">"{control.comment}"</p>
                          )}
                          {/* Control Photos */}
                          {control.control_photos && control.control_photos.length > 0 && (
                            <div className="flex gap-1 mt-2">
                              {control.control_photos.slice(0, 4).map((photo, idx) => (
                                <div 
                                  key={`hist-photo-${idx}`}
                                  className="w-12 h-12 rounded-lg overflow-hidden cursor-pointer"
                                  onClick={() => setSelectedPhoto(getPhotoUrl(photo))}
                                >
                                  <img 
                                    src={getPhotoUrl(photo)} 
                                    alt=""
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              ))}
                              {control.control_photos.length > 4 && (
                                <div className="w-12 h-12 rounded-lg bg-slate-200 flex items-center justify-center text-xs text-slate-600">
                                  +{control.control_photos.length - 4}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <Badge className={`${
                          control.result === 'validated' 
                            ? 'bg-emerald-500 text-white' 
                            : 'bg-red-500 text-white'
                        } border-0`}>
                          {control.result === 'validated' ? 'Validé' : 'Non-conforme'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Photo Viewer Modal */}
      {selectedPhoto && (
        <div 
          className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <Button 
            variant="ghost" 
            size="icon"
            className="absolute top-4 right-4 text-white hover:bg-white/20"
            onClick={() => setSelectedPhoto(null)}
          >
            <X className="h-6 w-6" />
          </Button>
          <img 
            src={selectedPhoto}
            alt="Photo agrandie"
            className="max-w-full max-h-full object-contain"
          />
        </div>
      )}

      {/* Task Selector Modal */}
      {showTaskSelector && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-800">Choisir un logement à contrôler</h2>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => setShowTaskSelector(false)}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
              <p className="text-sm text-slate-500 mt-2">
                Sélectionnez un ménage terminé aujourd'hui pour effectuer un contrôle qualité
              </p>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              {completedTodayTasks.length === 0 ? (
                <div className="text-center py-12">
                  <Home className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Aucun ménage terminé aujourd'hui</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {completedTodayTasks.map(task => (
                    <button
                      key={task.id}
                      onClick={() => selectTaskForControl(task)}
                      className="w-full p-4 bg-slate-50 hover:bg-emerald-50 rounded-xl border border-slate-200 hover:border-emerald-300 transition-colors text-left"
                      data-testid={`select-task-${task.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div 
                          className="w-4 h-4 rounded-full flex-shrink-0 mt-1" 
                          style={{ backgroundColor: task.property_color || '#003366' }}
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-slate-800 truncate">
                            {task.property_name}
                          </h3>
                          <p className="text-sm text-slate-500">{task.property_address}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
                            <div className="flex items-center gap-1">
                              <Users className="h-4 w-4" />
                              {task.housekeeper_names?.join(' & ') || 'Non assigné'}
                            </div>
                            {task.time_spent_minutes > 0 && (
                              <div className="flex items-center gap-1">
                                <Clock className="h-4 w-4" />
                                {task.time_spent_minutes} min
                              </div>
                            )}
                          </div>
                          {task.photos?.length > 0 && (
                            <div className="flex items-center gap-1 mt-2 text-sm text-emerald-600">
                              <Camera className="h-4 w-4" />
                              {task.photos.length} photo(s)
                            </div>
                          )}
                        </div>
                        <ChevronRight className="h-5 w-5 text-slate-400" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GouvernantDashboard;
